import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

/** Where an access request stands, by the numbers the API sends. */
export enum AccessRequestStatus {
  Pending = 0,
  Approved = 1,
  Denied = 2,
}

const STATUS_BY_NAME: Readonly<Record<string, AccessRequestStatus>> = {
  Pending: AccessRequestStatus.Pending,
  Approved: AccessRequestStatus.Approved,
  Denied: AccessRequestStatus.Denied,
};

/**
 * `AccessRequestResponse` as `GET /permissions/access-requests` and its siblings answer it.
 *
 * The status crosses the wire as its ordinal: the API serializes with Newtonsoft and registers no
 * string-enum converter (`ApiJsonContract`), so a C# enum arrives as a number. The name is accepted
 * too, so a server that starts naming it does not blank the page. `resource` is kept as it came and
 * read by `readRequestableAccess`, which tells a request it can name from one it cannot.
 */
export default class AccessRequestSchema {
  public static readonly status = z.preprocess(
    (value) => (typeof value === "string" && value in STATUS_BY_NAME ? STATUS_BY_NAME[value] : value),
    z.enum(AccessRequestStatus),
  );

  public static readonly type = z.object({
    id: z.uuid(),
    requesterUserId: z.uuid(),
    requesterName: z.string(),
    resource: z.unknown(),
    accessDescription: z.string(),
    context: z.string(),
    returnPath: z.string().nullish(),
    status: AccessRequestSchema.status,
    createdDate: z.string(),
    decidedByUserId: z.uuid().nullish(),
    decidedByName: z.string().nullish(),
    decidedDate: z.string().nullish(),
    decisionNote: z.string().nullish(),
    callerMayDecide: z.boolean(),
  });

  public static validate(values: unknown): AccessRequestJSON {
    return parseOrThrow(AccessRequestSchema.type, values, {
      logError: true,
      schemaName: "AccessRequestSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is AccessRequestJSON {
    return AccessRequestSchema.type.safeParse(values).success;
  }
}

export type AccessRequestJSON = z.infer<typeof AccessRequestSchema.type>;
